/**
 * Analytics Page
 * Comprehensive analytics dashboard for monitoring usage metrics and performance
 */
import React, { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '../../components/layout';
import { 
  analyticsApi, 
  AnalyticsDashboardData, 
  AnalyticsFilters, 
  AnalyticsTimePeriod,
  SummaryMetrics,
  ModelTokenUsage,
} from '../../services/api';
import { Spinner } from '../../components/ui';
import styles from './AnalyticsPage.module.css';
import { 
  ChevronDown,
  X,
  RefreshCw,
  Maximize2,
  Info,
} from 'lucide-react';

// Time period options
const TIME_PERIODS: { value: AnalyticsTimePeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_60_days', label: 'Last 60 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

// Format currency
const formatCurrency = (value: number): string => {
  return `$${value.toFixed(2)}`;
};

// Format large numbers
const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string | number;
  description?: string;
  trend?: number;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, description, trend }) => (
  <div className={styles.metricCard}>
    <div className={styles.metricTitle}>{title}</div>
    <div className={styles.metricValue}>{value}</div>
    {description && <div className={styles.metricDescription}>{description}</div>}
    {trend !== undefined && (
      <div className={`${styles.metricTrend} ${trend >= 0 ? styles.positive : styles.negative}`}>
        {trend >= 0 ? '+' : ''}{trend}%
      </div>
    )}
  </div>
);

// Section Header Component
interface SectionHeaderProps {
  title: string;
  onExpand?: () => void;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, onExpand }) => (
  <div className={styles.sectionHeader}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    {onExpand && (
      <button className={styles.expandButton} onClick={onExpand}>
        <Maximize2 size={16} />
      </button>
    )}
  </div>
);

// Data Table Component
interface DataTableProps {
  columns: { key: string; label: string; align?: 'left' | 'right' | 'center' }[];
  data: Record<string, any>[];
  emptyMessage?: string;
}

const DataTable: React.FC<DataTableProps> = ({ columns, data, emptyMessage = 'No data available' }) => (
  <div className={styles.tableContainer}>
    <table className={styles.dataTable}>
      <thead>
        <tr>
          {columns.map(col => (
            <th key={col.key} style={{ textAlign: col.align || 'left' }}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className={styles.emptyCell}>{emptyMessage}</td>
          </tr>
        ) : (
          data.map((row, idx) => (
            <tr key={idx}>
              {columns.map(col => (
                <td key={col.key} style={{ textAlign: col.align || 'left' }}>
                  {row[col.key]}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// Donut Chart Component
interface DonutChartProps {
  data: { label: string; value: number; percentage: number }[];
  emptyMessage?: string;
}

const DonutChart: React.FC<DonutChartProps> = ({ data, emptyMessage = 'No data available' }) => {
  if (data.length === 0) {
    return <div className={styles.emptyChart}>{emptyMessage}</div>;
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#1d4ed8', '#06b6d4'];
  
  // Calculate stroke dasharray for donut segments
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  let currentOffset = 0;

  return (
    <div className={styles.donutContainer}>
      <svg className={styles.donutChart} viewBox="0 0 200 200">
        {data.map((item, idx) => {
          const strokeLength = (item.percentage / 100) * circumference;
          const segment = (
            <circle
              key={idx}
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke={colors[idx % colors.length]}
              strokeWidth="24"
              strokeDasharray={`${strokeLength} ${circumference - strokeLength}`}
              strokeDashoffset={-currentOffset}
              transform="rotate(-90 100 100)"
            />
          );
          currentOffset += strokeLength;
          return segment;
        })}
        <text x="100" y="100" textAnchor="middle" dominantBaseline="middle" className={styles.donutCenter}>
          {total > 0 && `${data[0]?.percentage.toFixed(1)}%`}
        </text>
      </svg>
      <div className={styles.donutLegend}>
        {data.map((item, idx) => (
          <div key={idx} className={styles.legendItem}>
            <span className={styles.legendColor} style={{ backgroundColor: colors[idx % colors.length] }} />
            <span className={styles.legendLabel}>{item.label}</span>
            <span className={styles.legendValue}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AnalyticsPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<AnalyticsDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [timePeriod, setTimePeriod] = useState<AnalyticsTimePeriod>('last_60_days');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [meOnly, setMeOnly] = useState(false);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filters: AnalyticsFilters = {
        timePeriod,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        meOnly,
      };
      
      const response = await analyticsApi.getDashboard(filters);
      if (response.success) {
        setDashboardData(response.data);
      } else {
        setError('Failed to load analytics data');
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [timePeriod, startDate, endDate, meOnly]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Clear all filters
  const clearFilters = () => {
    setTimePeriod('last_60_days');
    setStartDate('');
    setEndDate('');
    setSelectedProject('');
    setMeOnly(false);
  };

  // Render sidebar content
  const renderSidebar = () => (
    <div className={styles.filtersSidebar}>
      <div className={styles.filtersHeader}>
        <h2 className={styles.filtersTitle}>FILTERS</h2>
        <button className={styles.clearFilters} onClick={clearFilters}>
          <X size={14} /> Clear all
        </button>
      </div>

      {/* Time Period */}
      <div className={styles.filterGroup}>
        <div className={styles.filterLabel}>
          <ChevronDown size={14} /> TIME PERIOD
        </div>
        <select 
          className={styles.filterSelect}
          value={timePeriod}
          onChange={(e) => setTimePeriod(e.target.value as AnalyticsTimePeriod)}
        >
          {TIME_PERIODS.map(period => (
            <option key={period.value} value={period.value}>{period.label}</option>
          ))}
        </select>
      </div>

      {/* Start Date */}
      <div className={styles.filterGroup}>
        <div className={styles.filterLabel}>
          <ChevronDown size={14} /> START DATE
        </div>
        <input
          type="datetime-local"
          className={styles.filterInput}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Select start date and time"
        />
      </div>

      {/* End Date */}
      <div className={styles.filterGroup}>
        <div className={styles.filterLabel}>
          <ChevronDown size={14} /> END DATE
        </div>
        <input
          type="datetime-local"
          className={styles.filterInput}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          placeholder="Select end date and time"
        />
      </div>

      {/* Project */}
      <div className={styles.filterGroup}>
        <div className={styles.filterLabel}>
          <ChevronDown size={14} /> PROJECT
        </div>
        <select 
          className={styles.filterSelect}
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
        >
          <option value="">Project</option>
        </select>
      </div>

      {/* Users */}
      <div className={styles.filterGroup}>
        <div className={styles.filterLabel}>
          <ChevronDown size={14} /> USERS
        </div>
        <div className={styles.checkboxGroup}>
          <label className={styles.checkbox}>
            <input 
              type="checkbox" 
              checked={meOnly} 
              onChange={(e) => setMeOnly(e.target.checked)} 
            />
            <span>Me</span>
            <Info size={14} className={styles.infoIcon} />
          </label>
        </div>
        <select className={styles.filterSelect}>
          <option value="">Users</option>
        </select>
      </div>
    </div>
  );

  // Main content
  const renderMainContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingState}>
          <Spinner size="lg" label="Loading analytics data..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.errorState}>
          <p>{error}</p>
          <button onClick={loadDashboard} className={styles.retryButton}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      );
    }

    if (!dashboardData) {
      return null;
    }

    const { summary, topAssistants, workflowPerformance, llmModelDistribution, embeddingModelDistribution, toolUsage, agentUsage, webhookInvocations, userSpending, projectSpending, mcpServerUsage, mcpServersByUser, cliMetrics, modelTokenUsage } = dashboardData;

    return (
      <div className={styles.dashboardContent}>
        {/* Overview Section */}
        <section className={styles.section}>
          <h2 className={styles.mainSectionTitle}>Overview</h2>
          
          <div className={styles.card}>
            <SectionHeader title="Summary Metrics" />
            <p className={styles.cardDescription}>High-level overview of usage and costs</p>
            
            <div className={styles.metricsGrid}>
              <MetricCard 
                title="LLM Input Tokens" 
                value={formatNumber(summary.llmInputTokens)}
                description="Total input tokens for LLM operations (conversations, workflows, CLI)"
              />
              <MetricCard 
                title="LLM Cached Input Tokens" 
                value={formatNumber(summary.llmCachedInputTokens)}
                description="Total cached input tokens (prompt caching) for LLM operations"
              />
              <MetricCard 
                title="LLM Output Tokens" 
                value={formatNumber(summary.llmOutputTokens)}
                description="Total output tokens generated by LLM operations"
              />
              <MetricCard 
                title="Total Money Spent" 
                value={formatCurrency(summary.totalMoneySpent)}
                description="Total cost in USD across all usage types"
              />
              <MetricCard 
                title="Unique Active Users" 
                value={summary.uniqueActiveUsers}
                description="Number of distinct users who have interacted with the system"
              />
              <MetricCard 
                title="Unique Assistants Invoked" 
                value={summary.uniqueAssistantsInvoked}
                description="Number of distinct assistants that have been invoked"
              />
              <MetricCard 
                title="Unique Workflows Invoked" 
                value={summary.uniqueWorkflowsInvoked}
                description="Number of distinct workflows that have been executed"
              />
              <MetricCard 
                title="LLM Cost" 
                value={formatCurrency(summary.llmCost)}
                description="Total cost in USD for LLM operations (conversations, workflows, CLI)"
              />
              <MetricCard 
                title="CLI Cost" 
                value={formatCurrency(summary.cliCost)}
                description="Total cost in USD for CLI operations"
              />
              <MetricCard 
                title="Embedding Input Tokens" 
                value={formatNumber(summary.embeddingInputTokens)}
                description="Total input tokens used for embedding operations"
              />
              <MetricCard 
                title="Embedding Cost" 
                value={formatCurrency(summary.embeddingCost)}
                description="Total cost in USD for embedding operations"
              />
            </div>
          </div>
        </section>

        {/* Assistants & Workflows Section */}
        <section className={styles.section}>
          <h2 className={styles.mainSectionTitle}>Assistants & Workflows</h2>
          
          <div className={styles.twoColumnGrid}>
            <div className={styles.card}>
              <SectionHeader title="Top Assistants" />
              <p className={styles.cardDescription}>Most used assistants by total messages</p>
              <DataTable
                columns={[
                  { key: 'assistantName', label: 'Assistant' },
                  { key: 'totalChats', label: 'Total Chats', align: 'right' },
                  { key: 'totalMessages', label: 'Total Messages', align: 'right' },
                ]}
                data={topAssistants.map(a => ({
                  assistantName: a.assistantName,
                  totalChats: a.totalChats,
                  totalMessages: a.totalMessages,
                }))}
              />
              {topAssistants.length > 0 && (
                <div className={styles.tableTotal}>
                  <span>Total</span>
                  <span>{topAssistants.reduce((sum, a) => sum + a.totalChats, 0)}</span>
                  <span>{topAssistants.reduce((sum, a) => sum + a.totalMessages, 0)}</span>
                </div>
              )}
            </div>

            <div className={styles.card}>
              <SectionHeader title="Workflows Performance" />
              <p className={styles.cardDescription}>Workflow execution metrics and success rates</p>
              <DataTable
                columns={[
                  { key: 'workflowName', label: 'Workflow Name' },
                  { key: 'totalRuns', label: 'Total Runs', align: 'right' },
                  { key: 'successCount', label: 'Success', align: 'right' },
                  { key: 'failedCount', label: 'Failed', align: 'right' },
                  { key: 'avgDuration', label: 'Avg Duration', align: 'right' },
                ]}
                data={workflowPerformance.map(w => ({
                  workflowName: w.workflowName,
                  totalRuns: w.totalRuns,
                  successCount: w.successCount,
                  failedCount: w.failedCount,
                  avgDuration: w.avgDurationMs ? `${(w.avgDurationMs / 1000).toFixed(1)}s` : 'N/A',
                }))}
              />
            </div>
          </div>
        </section>

        {/* LLM Usage Section */}
        <section className={styles.section}>
          <h2 className={styles.mainSectionTitle}>LLM Usage</h2>

          <div className={styles.card}>
            <SectionHeader title="LLM Model Distribution" />
            <p className={styles.cardDescription}>Distribution of LLM model usage by total requests</p>
            <DonutChart
              data={llmModelDistribution.map(m => ({
                label: m.model,
                value: m.totalRequests,
                percentage: m.percentage,
              }))}
            />
          </div>

          {/* Per-Model Token & Cost Breakdown */}
          {modelTokenUsage && modelTokenUsage.length > 0 && (
            <div className={styles.card}>
              <SectionHeader title="Model Token & Cost Breakdown" />
              <p className={styles.cardDescription}>
                Token consumption and cost split per model — Your Key vs BrainIQ Key
              </p>
              <DataTable
                columns={[
                  { key: 'model', label: 'Model' },
                  { key: 'provider', label: 'Provider' },
                  { key: 'totalRequests', label: 'Requests', align: 'right' },
                  { key: 'inputTokens', label: 'Input Tokens', align: 'right' },
                  { key: 'outputTokens', label: 'Output Tokens', align: 'right' },
                  { key: 'totalCost', label: 'Total Cost', align: 'right' },
                  { key: 'userKeyCost', label: 'Your Key Cost', align: 'right' },
                  { key: 'serverKeyCost', label: 'BrainIQ Key Cost', align: 'right' },
                  { key: 'avgCost', label: 'Avg/Req', align: 'right' },
                ]}
                data={modelTokenUsage.map(m => ({
                  model: m.model,
                  provider: m.provider,
                  totalRequests: m.totalRequests.toLocaleString(),
                  inputTokens: formatNumber(m.inputTokens),
                  outputTokens: formatNumber(m.outputTokens),
                  totalCost: formatCurrency(m.totalCost),
                  userKeyCost: m.userKeyCost > 0 ? formatCurrency(m.userKeyCost) : '—',
                  serverKeyCost: m.serverKeyCost > 0 ? formatCurrency(m.serverKeyCost) : '—',
                  avgCost: formatCurrency(m.avgCostPerRequest),
                }))}
              />
              {/* Totals row */}
              <div className={styles.modelTotalsRow}>
                <span>Totals</span>
                <span>{formatNumber(modelTokenUsage.reduce((s, m) => s + m.totalRequests, 0))} reqs</span>
                <span>{formatNumber(modelTokenUsage.reduce((s, m) => s + m.inputTokens + m.outputTokens, 0))} tokens</span>
                <span className={styles.totalCostHighlight}>
                  {formatCurrency(modelTokenUsage.reduce((s, m) => s + m.totalCost, 0))} total
                </span>
                <span>
                  Your Key: {formatCurrency(modelTokenUsage.reduce((s, m) => s + m.userKeyCost, 0))}
                </span>
                <span>
                  BrainIQ Key: {formatCurrency(modelTokenUsage.reduce((s, m) => s + m.serverKeyCost, 0))}
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Embeddings Usage Section */}
        <section className={styles.section}>
          <h2 className={styles.mainSectionTitle}>Embeddings Usage</h2>
          
          <div className={styles.card}>
            <SectionHeader title="Embedding Model Invocation Distribution" />
            <p className={styles.cardDescription}>Distribution of embedding model usage by invocation count</p>
            <DonutChart 
              data={embeddingModelDistribution.map(m => ({
                label: m.model,
                value: m.totalInvocations,
                percentage: m.percentage,
              }))}
            />
          </div>
        </section>

        {/* Tools & Agents Section */}
        <section className={styles.section}>
          <h2 className={styles.mainSectionTitle}>Tools & Agents</h2>
          
          <div className={styles.twoColumnGrid}>
            <div className={styles.card}>
              <SectionHeader title="Tool Usage" />
              <p className={styles.cardDescription}>Most invoked tools and their error rates</p>
              <DataTable
                columns={[
                  { key: 'toolName', label: 'Tool Name' },
                  { key: 'toolType', label: 'Tool Type' },
                  { key: 'totalInvocations', label: 'Total Invocations', align: 'right' },
                  { key: 'uniqueUsers', label: 'Unique', align: 'right' },
                ]}
                data={toolUsage.map(t => ({
                  toolName: t.toolName,
                  toolType: t.toolType,
                  totalInvocations: t.totalInvocations,
                  uniqueUsers: t.uniqueUsers,
                }))}
              />
            </div>

            <div className={styles.card}>
              <SectionHeader title="Agent Usage" />
              <p className={styles.cardDescription}>Agent invocations and performance</p>
              <DataTable
                columns={[
                  { key: 'assistantName', label: 'Assistant Name' },
                  { key: 'totalConversations', label: 'Total Conversations', align: 'right' },
                ]}
                data={agentUsage.map(a => ({
                  assistantName: a.assistantName,
                  totalConversations: a.totalConversations,
                }))}
              />
              {agentUsage.length > 0 && (
                <div className={styles.tableTotal}>
                  <span>Total</span>
                  <span>{agentUsage.reduce((sum, a) => sum + a.totalConversations, 0)}</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.card}>
            <SectionHeader title="Webhook Invocations" />
            <p className={styles.cardDescription}>Webhook usage by user and alias</p>
            <DataTable
              columns={[
                { key: 'userId', label: 'User' },
                { key: 'totalInvocations', label: 'Total Invocations', align: 'right' },
                { key: 'webhookAlias', label: 'Webhook Alias' },
              ]}
              data={webhookInvocations.map(w => ({
                userId: w.userName || w.userId,
                totalInvocations: w.totalInvocations,
                webhookAlias: w.webhookAlias,
              }))}
            />
          </div>
        </section>

        {/* Projects & Users Section */}
        <section className={styles.section}>
          <h2 className={styles.mainSectionTitle}>Projects & Users</h2>
          
          <div className={styles.twoColumnGrid}>
            <div className={styles.card}>
              <SectionHeader title="User Spending Distribution" />
              <p className={styles.cardDescription}>Spending distribution across users</p>
              <DonutChart 
                data={userSpending.map(u => ({
                  label: u.userName || u.userId,
                  value: u.totalSpent,
                  percentage: u.percentage,
                }))}
              />
            </div>

            <div className={styles.card}>
              <SectionHeader title="Project Spending Distribution" />
              <p className={styles.cardDescription}>Money spent per project</p>
              <DonutChart 
                data={projectSpending.map(p => ({
                  label: p.projectName || p.projectId,
                  value: p.totalSpent,
                  percentage: p.percentage,
                }))}
              />
            </div>
          </div>
        </section>

        {/* MCP Servers Section */}
        <section className={styles.section}>
          <h2 className={styles.mainSectionTitle}>MCP Servers</h2>
          
          <div className={styles.twoColumnGrid}>
            <div className={styles.card}>
              <SectionHeader title="MCP Server Usage" />
              <p className={styles.cardDescription}>Distribution of MCP server usage</p>
              <DonutChart 
                data={mcpServerUsage.map(m => ({
                  label: m.serverName,
                  value: m.totalRequests,
                  percentage: m.percentage,
                }))}
              />
            </div>

            <div className={styles.card}>
              <SectionHeader title="MCP Servers by Users" />
              <p className={styles.cardDescription}>MCP server usage broken down by user</p>
              <DataTable
                columns={[
                  { key: 'userName', label: 'User' },
                  { key: 'serverName', label: 'MCP Server' },
                  { key: 'totalRequests', label: 'Total Requests', align: 'right' },
                ]}
                data={mcpServersByUser.map(m => ({
                  userName: m.userName || m.userId,
                  serverName: m.serverName,
                  totalRequests: m.totalRequests,
                }))}
              />
            </div>
          </div>
        </section>

        {/* CLI Analytics Section */}
        <section className={styles.section}>
          <h2 className={styles.mainSectionTitle}>CLI Analytics</h2>
          
          <div className={styles.card}>
            <SectionHeader title="CLI Summary Metrics" />
            <p className={styles.cardDescription}>High-level overview of CLI usage</p>
            
            <div className={styles.metricsGrid}>
              <MetricCard title="Unique Users" value={cliMetrics.uniqueUsers} />
              <MetricCard title="Unique Sessions" value={cliMetrics.uniqueSessions} />
              <MetricCard title="Unique Repositories" value={cliMetrics.uniqueRepositories} />
              <MetricCard title="Input Tokens" value={formatNumber(cliMetrics.inputTokens)} />
              <MetricCard title="Output Tokens" value={formatNumber(cliMetrics.outputTokens)} />
              <MetricCard title="Cached Tokens Read" value={formatNumber(cliMetrics.cachedTokensRead)} />
              <MetricCard title="Total Cost" value={formatCurrency(cliMetrics.totalCost)} />
              <MetricCard title="Cache Read Cost" value={formatCurrency(cliMetrics.cacheReadCost)} />
              <MetricCard title="Cache Creation Cost" value={formatCurrency(cliMetrics.cacheCreationCost)} />
              <MetricCard title="CLI Invoked" value={cliMetrics.cliInvoked} />
              <MetricCard 
                title="CLI Avg Session (ms)" 
                value={cliMetrics.avgSessionDurationMs?.toFixed(0) || 'N/A'} 
              />
              <MetricCard 
                title="CLI Max Session Duration (ms)" 
                value={cliMetrics.maxSessionDurationMs?.toFixed(0) || 'N/A'} 
              />
              <MetricCard 
                title="Proxy Success Rate (%)" 
                value={cliMetrics.proxySuccessRate?.toFixed(1) || 'N/A'} 
              />
              <MetricCard title="Proxy Failed Calls" value={cliMetrics.proxyFailedCalls} />
              <MetricCard title="Net New Lines" value={formatNumber(cliMetrics.netNewLines)} />
              <MetricCard title="Total Created Files" value={cliMetrics.totalCreatedFiles} />
              <MetricCard title="Total Deleted Lines" value={formatNumber(cliMetrics.totalDeletedLines)} />
              <MetricCard title="Total Deleted Files" value={cliMetrics.totalDeletedFiles} />
              <MetricCard title="Total Modified Files" value={cliMetrics.totalModifiedFiles} />
              <MetricCard title="Total Prompts" value={cliMetrics.totalPrompts} />
            </div>
          </div>
        </section>
      </div>
    );
  };

  // Sidebar content for AppLayout (matching Chats/Assistants pattern)
  const analyticsSidebar = (
    <div className={styles.sidebar}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Analytics</h1>
        <p className={styles.pageDescription}>Monitor usage metrics and performance</p>
      </div>
      {renderSidebar()}
    </div>
  );

  return (
    <AppLayout sidebar={analyticsSidebar}>
      <div className={styles.mainContent}>
        <div className={styles.dashboardHeader}>
          <h2 className={styles.dashboardTitle}>Analytics Dashboard</h2>
          <button onClick={loadDashboard} className={styles.refreshButton} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? styles.spinning : ''} />
          </button>
        </div>
        {renderMainContent()}
      </div>
    </AppLayout>
  );
};

export default AnalyticsPage;
